import type { ReactNode } from "react";
import type { RouteObject } from "react-router";

import { RequireAuth } from "./auth/RequireAuth";
import { type AnyOf, RequirePermission } from "./auth/RequirePermission";
import { pageHandle, type Requirement, SECTIONS } from "./layout/nav";
import { RouteError } from "./layout/RouteError";
import Shell from "./layout/Shell";
import AccountPage from "./pages/Account";
import ConversationDetailPage from "./pages/ConversationDetail";
import ConversationsPage from "./pages/Conversations";
import CostsPage from "./pages/Costs";
import DevicesPage from "./pages/Devices";
import EnvironmentsPage from "./pages/Environments";
import HomePage from "./pages/Home";
import InvitePage from "./pages/Invite";
import LegacyWorkflowsPage from "./pages/LegacyWorkflows";
import NotFoundPage from "./pages/NotFound";
import OrgsPage from "./pages/Orgs";
import PlatformKeysPage from "./pages/PlatformKeys";
import ResourceLimitsPage from "./admin/resource-limits/ResourceLimitsPage";
import RolesPage from "./pages/Roles";
import RunDetailPage from "./pages/RunDetail";
import RunsPage from "./pages/Runs";
import SecretsPage from "./pages/Secrets";
import SettingsPage from "./pages/Settings";
import SignInPage from "./pages/SignIn";
import EditTaskGroupPage from "./pages/EditTaskGroup";
import TaskGroupDetailPage from "./pages/TaskGroupDetail";
import TaskGroupsPage from "./pages/TaskGroups";
import NewTaskGroupPage from "./pages/NewTaskGroup";
import NetworkListPage from "./pages/NetworkList";
import NetworkListsPage from "./pages/NetworkLists";
import TaskGroupNetworkPage from "./pages/TaskGroupNetwork";
import TaskGroupChatPage from "./pages/TaskGroupChat";
import TaskChatPage from "./pages/TaskChat";
import SkillsPage from "./pages/Skills";
import SkillsEditorPage from "./pages/SkillsEditor";
import TaskTypesPage from "./pages/TaskTypes";
import UsersPage from "./pages/Users";
import WorkflowsPage from "./pages/Workflows";
import WorkspacesPage from "./pages/Workspaces";
import WorkspaceTerminalLazy from "./pages/WorkspaceTerminalLazy";
import EnvironmentTerminalLazy from "./pages/EnvironmentTerminalLazy";
import EnvironmentVpnPage from "./pages/EnvironmentVpn";
import EnvironmentDetailPage from "./workloads/environment/EnvironmentDetail";
import WorkflowDetailPage from "./workloads/workflow/WorkflowDetail";


/** A page behind a permission (D51); the API enforces it, this spares the user the refusals. */
const gated = (needs: Requirement | AnyOf, page: ReactNode) => <RequirePermission permission={needs}>{page}</RequirePermission>;
/** Admins read every user's task groups; a user with groups of their own reads those. */
const GROUP_READERS: AnyOf = ["ViewAllTaskGroups", "ManageOwnTaskGroups"];
/** The organisation / environment model's pages, folded under Platform: the Super Admin's. */
const platform = (page: ReactNode) => gated("super-admin", page);

// The route shell: one entry per section, plus the pages that hang off one (a run, a
// conversation, a workspace terminal). Everything but signing in and accepting an invitation
// (D52) needs a signed-in session (D48); there is no sign-up page.
export const routes: RouteObject[] = [
  { path: "/sign-in", element: <SignInPage /> },
  { path: "/invite/:token", element: <InvitePage /> },
  {
    path: "/",
    errorElement: <RouteError />,
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: withErrorElements([
      { index: true, element: <HomePage />, handle: SECTIONS.home },
      // Product
      { path: "workflows", element: gated("ViewWorkflows", <WorkflowsPage />), handle: SECTIONS.workflows },
      {
        path: "workflows/:workflowId",
        // The graph builder brings React Flow; it loads with the editor.
        lazy: () => import("./pages/WorkflowEditor").then((m) => ({ element: gated("ViewWorkflows", <m.default />) })),
        handle: pageHandle("workflow", "product"),
      },
      { path: "task-types", element: gated("ManageTaskTypes", <TaskTypesPage />), handle: SECTIONS.taskTypes },
      { path: "skills", element: gated("ViewSkills", <SkillsPage />), handle: SECTIONS.skills },
      {
        path: "skills/:skillId",
        element: gated("ViewSkills", <SkillsEditorPage />),
        handle: pageHandle("skill", "product"),
      },
      { path: "task-groups", element: gated(GROUP_READERS, <TaskGroupsPage />), handle: SECTIONS.taskGroups },
      { path: "task-groups/new", element: gated("ManageOwnTaskGroups", <NewTaskGroupPage />), handle: pageHandle("newTaskGroup", "product") },
      {
        path: "task-groups/:groupId/network",
        element: gated(GROUP_READERS, <TaskGroupNetworkPage />),
        handle: pageHandle("taskGroupNetwork", "product"),
      },
      {
        path: "task-groups/:groupId/chat",
        element: gated("ManageOwnTaskGroups", <TaskGroupChatPage />),
        handle: pageHandle("taskGroupChat", "product"),
      },
      {
        path: "tasks/:taskId/chat",
        element: gated("ManageOwnTasks", <TaskChatPage />),
        handle: pageHandle("taskChat", "product"),
      },
      {
        path: "task-groups/:groupId/edit",
        element: gated("ManageOwnTaskGroups", <EditTaskGroupPage />),
        handle: pageHandle("taskGroup", "product"),
      },
      {
        path: "task-groups/:groupId",
        element: gated(GROUP_READERS, <TaskGroupDetailPage />),
        handle: pageHandle("taskGroup", "product"),
      },
      {
        path: "tasks/:taskId",
        // The task's graph brings React Flow; it loads with the page. Its owner opens it to rate it.
        lazy: () => import("./pages/TaskDetail").then((m) => ({ element: gated(["ViewAllTaskGroups", "ManageOwnTasks"], <m.default />) })),
        handle: pageHandle("task", "product"),
      },
      // Security and admin
      { path: "secrets", element: gated("ManageOwnSecrets", <SecretsPage />), handle: SECTIONS.secrets },
      { path: "devices", element: gated("ManageOwnDevices", <DevicesPage />), handle: SECTIONS.devices },
      { path: "network", element: gated("ManageOwnTaskGroups", <NetworkListsPage />), handle: SECTIONS.network },
      { path: "network/:listId", element: gated("ManageOwnTaskGroups", <NetworkListPage />), handle: pageHandle("networkList", "security") },
      { path: "users", element: gated("ViewUsers", <UsersPage />), handle: SECTIONS.users },
      { path: "roles", element: gated("ViewRoles", <RolesPage />), handle: SECTIONS.roles },
      { path: "platform-keys", element: gated("ManagePlatformCredentials", <PlatformKeysPage />), handle: SECTIONS.platformKeys },
      { path: "resource-limits", element: gated("ViewResourceLimits", <ResourceLimitsPage />), handle: SECTIONS.resourceLimits },
      { path: "costs", element: gated("ViewAllCosts", <CostsPage />), handle: SECTIONS.costs },
      { path: "settings", element: gated("ViewWorkflows", <SettingsPage />), handle: SECTIONS.settings },
      { path: "account", element: <AccountPage />, handle: SECTIONS.account },
      // Platform
      { path: "orgs", element: platform(<OrgsPage />), handle: SECTIONS.orgs },
      { path: "environments", element: platform(<EnvironmentsPage />), handle: SECTIONS.environments },
      {
        path: "environments/:envId",
        element: platform(<EnvironmentDetailPage />),
        handle: pageHandle("environment", "platform"),
      },
      { path: "workspaces", element: platform(<WorkspacesPage />), handle: SECTIONS.workspaces },
      // D79: any signed-in user's own environment; the API authorises the shell itself.
      {
        path: "environments/:envId/terminal",
        element: <EnvironmentTerminalLazy />,
        handle: pageHandle("environmentTerminal", "product", false),
      },
      // D80: the owner's environment VPN; the API authorises.
      { path: "environments/:envId/vpn", element: <EnvironmentVpnPage />, handle: pageHandle("environmentVpn", "product") },
      {
        path: "workspaces/:workspaceId/terminal",
        element: platform(<WorkspaceTerminalLazy />),
        handle: pageHandle("terminal", "platform", false),
      },
      { path: "legacy-workflows", element: platform(<LegacyWorkflowsPage />), handle: SECTIONS.legacyWorkflows },
      {
        path: "legacy-workflows/:workflowId",
        element: platform(<WorkflowDetailPage />),
        handle: pageHandle("legacyWorkflow", "platform"),
      },
      { path: "runs", element: platform(<RunsPage />), handle: SECTIONS.runs },
      {
        path: "runs/:runId",
        element: platform(<RunDetailPage />),
        handle: pageHandle("run", "platform"),
      },
      // Chats (D82) made conversations every task-group user's: the list links group chats.
      { path: "conversations", element: gated(["ManageOwnTaskGroups", "ManageOwnTasks"], <ConversationsPage />), handle: SECTIONS.conversations },
      {
        path: "conversations/:conversationId",
        element: gated(["ManageOwnTaskGroups", "ManageOwnTasks"], <ConversationDetailPage />),
        handle: pageHandle("conversation", "product"),
      },
      // Design previews of the run page and a group chat with sample decrypted conversations.
      ...(import.meta.env.DEV
        ? [
            {
              path: "preview/runs/:runId",
              lazy: () => import("./workloads/preview/RunPreview").then((m) => ({ Component: m.default })),
              handle: pageHandle("runPreview", "platform"),
            },
            {
              path: "preview/task-groups/:groupId/chat",
              lazy: () => import("./chat/preview/ChatPreview").then((m) => ({ Component: m.default })),
              handle: pageHandle("taskGroupChat", "product"),
            },
          ]
        : []),
      { path: "*", element: <NotFoundPage />, handle: pageHandle("notFound", undefined, false) },
    ]),
  },
];

/** Each page renders its errors inside the shell (S65). */
function withErrorElements(children: RouteObject[]): RouteObject[] {
  return children.map((route) => ({ errorElement: <RouteError />, ...route }));
}
